import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { venueSeatingAreaSchema } from '../models/schema'
import { VenueSeatingArea } from '../models/VenueSeatingArea'
import {
  fetchVenueSeatingAreas,
  updateVenueSeatingArea as updateAPI,
} from '../services/VenueSeatingAreaService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<VenueSeatingArea> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'venueSeatingArea',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getVenueSeatingAreasSuccess,
  setQuery: setVenueSeatingAreaQuery,
  reset: resetVenueSeatingAreaState,
  resetQueryAndIds: resetVenueSeatingAreaQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: VenueSeatingArea }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectVenueSeatingAreas = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedVenueSeatingArea = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectVenueSeatingAreaById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectVenueSeatingAreasByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedVenueSeatingArea =
  (venueSeatingArea: VenueSeatingArea | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<VenueSeatingArea>(venueSeatingArea)
      )
    )

export const getVenueSeatingAreas = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchVenueSeatingAreas(params),
    [venueSeatingAreaSchema],
    slice.actions
  )

export const updateVenueSeatingAreas = (venueSeatingArea: VenueSeatingArea) =>
  SBAPI.UpdateDispatched<VenueSeatingArea>(
    updateAPI(venueSeatingArea),
    venueSeatingArea,
    slice.actions
  )
